// src/render.js
// Builds the markup mirroring ../visamundi-widget/views/public/widget-base.php
// (title, two country pickers, polite live region) inside an isolated shadow root.
// The native <select>s are replaced by a custom combobox so each option can show
// a round country flag (see combobox.js).
import styles from './styles.css';
import logoLight from './logo-visamundi.png'; // navy wordmark, for light theme
import logoDark from './logo-visamundi-white.png'; // white wordmark, for dark theme
import mascotLooping from './mascot-looping.webp'; // Looping + passport, 2x asset from visamundi-website
import { t } from './i18n.js';
import { createCombobox } from './combobox.js';
import { flagUrl } from './iso.js';

// Builds the shadow DOM skeleton; returns references to the live elements.
export function renderSkeleton(host, { language: lang, theme, size }) {
  const root = host.attachShadow ? host.attachShadow({ mode: 'open' }) : host;

  const style = document.createElement('style');
  style.textContent = styles;
  root.appendChild(style);

  const wrap = document.createElement('div');
  wrap.className = 'vm';
  wrap.setAttribute('data-theme', theme === 'dark' ? 'dark' : 'light');
  if (size === 'compact') wrap.setAttribute('data-size', 'compact');

  const logo = theme === 'dark' ? logoDark : logoLight;
  // Header band: title (with logo) on a mist tint, Looping anchored bottom-right
  // on a pastel halo. The mascot is the widget's only warmth (visamundi DESIGN.md).
  const header = document.createElement('div');
  header.className = 'vm__head';
  const titleEl = document.createElement('p');
  titleEl.className = 'vm__title';
  titleEl.innerHTML = t(
    lang,
    'titleTemplate',
    `<strong>${escapeHtml(t(lang, 'visaWord'))}</strong>`,
    `<span class="vm__logo-wrap"><img class="vm__logo" src="${logo}" alt="Visamundi" /></span>`,
  );
  const mascotWrap = document.createElement('div');
  mascotWrap.className = 'vm__mascot';
  mascotWrap.setAttribute('aria-hidden', 'true');
  const mascot = document.createElement('img');
  mascot.src = mascotLooping;
  mascot.alt = '';
  mascot.width = 120;
  mascot.height = 104;
  mascot.decoding = 'async';
  mascotWrap.appendChild(mascot);
  header.append(titleEl, mascotWrap);

  const body = document.createElement('div');
  body.className = 'vm__body';

  const form = document.createElement('div');
  form.className = 'vm__form';
  const nationality = createCombobox({
    label: t(lang, 'nationalityQuestion'),
    placeholder: t(lang, 'choose'),
  });
  const destination = createCombobox({
    label: t(lang, 'destinationQuestion'),
    placeholder: t(lang, 'choose'),
  });
  // Flight-path motif between origin and destination — the brand signature, and
  // a literal read of the journey the widget answers (passport → destination).
  const path = document.createElement('div');
  path.className = 'vm__path';
  path.setAttribute('aria-hidden', 'true');
  path.innerHTML =
    '<span class="vm__path-plane"><svg viewBox="0 0 24 24">' +
    '<path fill="currentColor" d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z"/>' +
    '</svg></span>';
  form.append(
    field(t(lang, 'nationality'), nationality),
    path,
    field(t(lang, 'destination'), destination),
  );

  const result = document.createElement('div');
  result.className = 'vm__result';
  result.setAttribute('aria-live', 'polite');

  body.append(form, result);
  wrap.append(header, body);
  root.appendChild(wrap);

  return { nationality, destination, result, mascot: mascotWrap };
}

// A labelled field: small uppercase label above its combobox.
function field(labelText, control) {
  const f = document.createElement('div');
  f.className = 'vm__field';
  const lbl = document.createElement('span');
  lbl.className = 'vm__field-label';
  lbl.textContent = labelText;
  f.append(lbl, control.root);
  return f;
}

// Fills the given comboboxes with country options and applies an optional preselect.
export function fillCountries(controls, countries, preselect) {
  controls.forEach((c) => c.setOptions(countries, preselect));
}

// Renders the result as a structured card: a colour-coded status badge, the
// destination (flag + name), the plain-language statement, then the CTAs.
// `opts` = { extra?: {label,url}, destination?: {name, alpha3} }.
export function renderResult(resultEl, formatted, opts = {}) {
  const { extra, destination } = opts;
  resultEl.removeAttribute('aria-busy');
  resultEl.innerHTML = '';
  const card = document.createElement('div');
  card.className = 'vm__card';

  if (formatted.status) {
    const badge = document.createElement('span');
    badge.className = `vm__badge vm__badge--${formatted.status.tone}`;
    const dot = document.createElement('span');
    dot.className = 'vm__badge-dot';
    dot.setAttribute('aria-hidden', 'true');
    badge.append(dot, document.createTextNode(formatted.status.label));
    card.appendChild(badge);
  }

  if (destination && destination.name) {
    const dest = document.createElement('div');
    dest.className = 'vm__dest';
    const url = flagUrl(destination.alpha3, 40);
    if (url) {
      const img = document.createElement('img');
      img.className = 'vm__flag vm__flag--lg';
      img.src = url;
      img.srcset = `${flagUrl(destination.alpha3, 80)} 2x`;
      img.alt = '';
      dest.appendChild(img);
    }
    const nm = document.createElement('strong');
    nm.textContent = destination.name;
    dest.appendChild(nm);
    card.appendChild(dest);
  }

  const p = document.createElement('p');
  p.className = 'vm__statement';
  p.textContent = formatted.text;
  card.appendChild(p);

  const ctas = document.createElement('div');
  ctas.className = 'vm__ctas';
  if (formatted.button) ctas.appendChild(makeCta(formatted.button, 'vm__cta'));
  if (extra && extra.url) ctas.appendChild(makeCta(extra, 'vm__cta vm__cta--alt'));
  if (ctas.childNodes.length) card.appendChild(ctas);

  // Stagger index for the reveal (badge, destination, statement, CTAs).
  Array.from(card.children).forEach((el, i) => el.style.setProperty('--vm-i', i));
  card.classList.add('vm__card--reveal');
  resultEl.appendChild(card);
}

function makeCta({ url, label }, className) {
  const a = document.createElement('a');
  a.className = className;
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener';
  a.textContent = label;
  return a;
}

// Placeholder shown while a lookup is in flight (keeps the height stable).
export function renderLoading(resultEl, label) {
  resultEl.innerHTML = '';
  resultEl.setAttribute('aria-busy', 'true');
  const card = document.createElement('div');
  card.className = 'vm__card vm__card--loading';
  card.innerHTML =
    '<span class="vm__sk vm__sk--badge"></span>' +
    '<span class="vm__sk vm__sk--title"></span>' +
    '<span class="vm__sk"></span><span class="vm__sk vm__sk--short"></span>';
  const sr = document.createElement('span');
  sr.className = 'vm__sr';
  sr.textContent = label;
  card.appendChild(sr);
  resultEl.appendChild(card);
}

// Looping nods once when an answer lands. Re-triggerable: the class is removed
// and the reflow restarts the animation.
export function cheerMascot(mascotEl) {
  if (!mascotEl) return;
  mascotEl.classList.remove('is-cheering');
  void mascotEl.offsetWidth;
  mascotEl.classList.add('is-cheering');
}

export function renderError(resultEl, message) {
  resultEl.removeAttribute('aria-busy');
  resultEl.innerHTML = '';
  const card = document.createElement('div');
  card.className = 'vm__card';
  const p = document.createElement('p');
  p.className = 'vm__statement vm__error';
  p.textContent = message;
  card.appendChild(p);
  resultEl.appendChild(card);
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]),
  );
}
